import React, { useEffect, useState, useCallback } from 'react';
import {
  Card,
  Table,
  Button,
  Tag,
  Space,
  Input,
  Modal,
  Form,
  Select,
  Avatar,
  message,
  Typography,
  Row,
  Col,
  Popconfirm,
  Tooltip,
} from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  UserOutlined,
  MailOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { tenantApi } from '../../services/tenant';
import { useAuthStore } from '../../stores/authStore';
import type { Member, InviteMemberRequest, UpdateMemberRoleRequest, InvitationResponse } from '../../types/tenant';

// 扩展邀请响应类型（包含invite_url）
interface InviteMemberResponse extends InvitationResponse {
  invite_url?: string;
}
import type { PaginationParams } from '../../types/api';
import { COLORS } from '../../constants/colors';

const { Title, Text } = Typography;
const { Option } = Select;

// 角色配置：颜色和文本
const roleConfig: Record<string, { color: string; text: string }> = {
  owner: { color: 'purple', text: '所有者' },
  admin: { color: 'blue', text: '管理员' },
  operator: { color: 'green', text: '运营' },
  viewer: { color: 'default', text: '查看者' },
};

// 状态配置
const statusConfig: Record<string, { color: string; text: string }> = {
  active: { color: 'success', text: '正常' },
  inactive: { color: 'default', text: '未激活' },
  pending: { color: 'processing', text: '待确认' },
};

export const MemberManagement: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<Member[]>([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [modalVisible, setModalVisible] = useState(false);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [form] = Form.useForm();
  
  const { user } = useAuthStore();

  // 是否有管理权限（只有 Owner 和 Admin 可以管理成员）
  const canManage = user?.role === 'owner' || user?.role === 'admin';

  // 获取成员列表
  const fetchMembers = useCallback(async () => {
    setLoading(true);
    try {
      const params: PaginationParams & { search?: string } = {
        page: currentPage,
        page_size: pageSize,
      };
      if (searchQuery) {
        params.search = searchQuery;
      }
      const response = await tenantApi.getMembers(params);
      setData(response.data.list || []);
      setTotal(response.data.meta?.total || 0);
    } catch {
      message.error('获取成员列表失败');
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, searchQuery]);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  // 搜索防抖
  const handleSearch = (value: string) => {
    setSearchQuery(value);
    setCurrentPage(1);
  };

  // 打开邀请成员弹窗
  const handleInvite = () => {
    form.resetFields();
    setModalVisible(true);
  };

  // 邀请成员
  const handleInviteSubmit = async (values: InviteMemberRequest) => {
    setInviteLoading(true);
    try {
      const response = await tenantApi.inviteMember(values);
      setModalVisible(false);
      form.resetFields();
      message.success(`邀请已发送至 ${values.email}`);
      // 显示邀请链接（开发环境）
      const inviteUrl = (response.data as InviteMemberResponse).invite_url;
      if (inviteUrl) {
        Modal.info({
          title: '邀请链接',
          content: (
            <div>
              <p>邀请链接已生成：</p>
              <Input.TextArea 
                value={inviteUrl} 
                autoSize 
                readOnly 
                style={{ marginTop: 8 }}
              />
            </div>
          ),
        });
      }
      fetchMembers();
    } catch {
      message.error('邀请失败，请重试');
    } finally {
      setInviteLoading(false);
    }
  };

  // 修改成员角色
  const handleRoleChange = async (memberId: string, newRole: string, memberName: string) => {
    try {
      const data: UpdateMemberRoleRequest = {
        role: newRole as 'admin' | 'operator' | 'viewer',
      };
      await tenantApi.updateMemberRole(memberId, data);
      message.success(`已将 ${memberName} 的角色修改为 ${roleConfig[newRole]?.text || newRole}`);
      fetchMembers();
    } catch {
      message.error('修改角色失败');
    }
  };

  // 移除成员
  const handleRemove = async (memberId: string, memberName: string) => {
    try {
      await tenantApi.removeMember(memberId);
      message.success(`已移除成员 ${memberName}`);
      fetchMembers();
    } catch {
      message.error('移除成员失败');
    }
  };

  // 获取角色Tag
  const getRoleTag = (role: string) => {
    const config = roleConfig[role] || { color: 'default', text: role };
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  // 获取状态Tag
  const getStatusTag = (status: string) => {
    const config = statusConfig[status] || { color: 'default', text: status };
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  // 格式化日期
  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    return dateStr.split('T')[0];
  };

  // 表格列定义
  const columns = [
    {
      title: '成员',
      dataIndex: 'name',
      key: 'name',
      width: 200,
      render: (text: string, record: Member) => (
        <Space>
          <Avatar icon={<UserOutlined />} src={(record as Member & { avatar_url?: string }).avatar_url || record.avatar || undefined}>
            {text?.[0]?.toUpperCase()}
          </Avatar>
          <div>
            <div style={{ fontWeight: 500 }}>{text || '未设置姓名'}</div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {record.email}
            </Text>
          </div>
        </Space>
      ),
    },
    {
      title: '角色',
      dataIndex: 'role',
      key: 'role',
      width: 150,
      render: (role: string, record: Member) => {
        // Owner角色不可修改
        if (role === 'owner') {
          return getRoleTag(role);
        }
        // 只有管理员可以修改角色，且不能修改自己的角色
        if (!canManage || record.user_id === user?.id) {
          return getRoleTag(role);
        }
        return (
          <Select
            value={role}
            style={{ width: 120 }}
            size="small"
            onChange={(value) => handleRoleChange(record.id, value, record.name)}
            options={[
              { value: 'admin', label: <Tag color="blue">管理员</Tag> },
              { value: 'operator', label: <Tag color="green">运营</Tag> },
              { value: 'viewer', label: <Tag color="default">查看者</Tag> },
            ]}
          />
        );
      },
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => getStatusTag(status),
    },
    {
      title: '加入时间',
      dataIndex: 'joined_at',
      key: 'joined_at',
      width: 120,
      render: (date: string) => formatDate(date),
    },
    {
      title: '最后活跃',
      dataIndex: 'last_active_at',
      key: 'last_active_at',
      width: 120,
      render: (date: string) => formatDate(date),
    },
    {
      title: '操作',
      key: 'action',
      width: 100,
      render: (_: unknown, record: Member) => {
        // Owner角色不可移除
        if (record.role === 'owner') {
          return (
            <Tooltip title="所有者不可被移除">
              <Button type="text" disabled icon={<DeleteOutlined />}>
                移除
              </Button>
            </Tooltip>
          );
        }
        // 不能移除自己
        if (record.user_id === user?.id) {
          return (
            <Tooltip title="不能移除自己">
              <Button type="text" disabled icon={<DeleteOutlined />}>
                移除
              </Button>
            </Tooltip>
          );
        }
        // 非管理员不能操作
        if (!canManage) {
          return null;
        }
        return (
          <Popconfirm
            title="确认移除成员"
            description={`确定要移除成员 ${record.name} 吗？移除后该成员将无法访问系统。`}
            onConfirm={() => handleRemove(record.id, record.name)}
            okText="确认移除"
            cancelText="取消"
            okButtonProps={{ danger: true }}
          >
            <Button type="text" danger icon={<DeleteOutlined />}>
              移除
            </Button>
          </Popconfirm>
        );
      },
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 24 }}>
        <Title level={4} style={{ margin: 0 }}>
          成员管理
        </Title>
        {canManage && (
          <Button type="primary" icon={<PlusOutlined />} onClick={handleInvite}>
            邀请成员
          </Button>
        )}
      </div>

      <Card style={{ marginBottom: 24 }}>
        <Row gutter={16} align="middle">
          <Col span={8}>
            <Input.Search
              placeholder="搜索成员姓名或邮箱"
              enterButton={<SearchOutlined />}
              onSearch={handleSearch}
              allowClear
            />
          </Col>
          <Col span={16} style={{ textAlign: 'right' }}>
            <Space split={<span style={{ color: COLORS.borderLight }}>|</span>}>
              <Text type="secondary">共 {total} 位成员</Text>
              <Text type="secondary">席位：{data.length} / 10</Text>
            </Space>
          </Col>
        </Row>
      </Card>

      <Card>
        <Table
          columns={columns}
          dataSource={data}
          rowKey="id"
          loading={loading}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条`,
            onChange: (page, size) => {
              setCurrentPage(page);
              setPageSize(size);
            },
          }}
          locale={{ emptyText: '暂无成员数据' }}
        />
      </Card>

      {/* 邀请成员弹窗 */}
      <Modal
        title="邀请成员"
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={480}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleInviteSubmit}
        >
          <Form.Item
            name="email"
            label="邮箱地址"
            rules={[
              { required: true, message: '请输入邮箱地址' },
              {
                validator: (_: unknown, value: string) => {
                  if (!value || /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value)) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('请输入有效的邮箱地址'));
                },
              },
            ]}
          >
            <Input prefix={<MailOutlined />} placeholder="member@company.com" />
          </Form.Item>

          <Form.Item
            name="role"
            label="角色"
            rules={[{ required: true, message: '请选择角色' }]}
          >
            <Select placeholder="选择角色">
              <Option value="admin">
                <Space>
                  <Tag color="blue">管理员</Tag>
                  <Text type="secondary" style={{ fontSize: 12 }}>拥有所有权限</Text>
                </Space>
              </Option>
              <Option value="operator">
                <Space>
                  <Tag color="green">运营</Tag>
                  <Text type="secondary" style={{ fontSize: 12 }}>可执行日常运营操作</Text>
                </Space>
              </Option>
              <Option value="viewer">
                <Space>
                  <Tag color="default">查看者</Tag>
                  <Text type="secondary" style={{ fontSize: 12 }}>仅可查看数据</Text>
                </Space>
              </Option>
            </Select>
          </Form.Item>

          <Form.Item>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button onClick={() => setModalVisible(false)}>取消</Button>
              <Button type="primary" htmlType="submit" loading={inviteLoading}>
                发送邀请
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 权限说明 */}
      {!canManage && (
        <Card style={{ marginTop: 16 }}>
          <Space>
            <ExclamationCircleOutlined style={{ color: COLORS.warning }} />
            <Text type="secondary">
              只有所有者和管理员可以邀请和管理成员
            </Text>
          </Space>
        </Card>
      )}
    </div>
  );
};
